/**
 * Stage 6: run a version on a real stored run, writing nothing, and show every
 * outcome with its reason.
 */

import { useState } from "react";
import { FlaskConical } from "lucide-react";

import { Banner, Button, Field, FieldRow, Select, SkeletonRows, TryReport } from "@/components/kit";
import {
  getIntegrationScoutJob,
  isScoutDown,
  listRuns,
  tryVersion,
  type Run,
} from "@/lib/integrations/api";
import { guardChecks, usualRows } from "@/lib/integrations/guards";
import type { Settings } from "@/lib/integrations/settings";
import { when } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

import { useKeyed } from "../hooks";
import { RunRows } from "../RunRows";

export function TryStage({
  code,
  versions,
  version: startVersion,
  safety,
  timeZone,
  onTried,
  since,
}: {
  code: string;
  /** The versions a person may try, newest first. */
  versions: { number: number; label: string }[];
  version: number | null;
  safety: Settings["safety"];
  timeZone?: string;
  onTried?: (run: Run) => void;
  /** Only show an earlier try made after this moment (a draft saved later makes an older try out of date). */
  since?: string | null;
}) {
  const [version, setVersion] = useState<number | null>(startVersion);
  const [run, setRun] = useState("");
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState<{ text: string; scout: boolean } | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const job = useKeyed(`${code}|job`, () => getIntegrationScoutJob(code));
  const runs = useKeyed(`${code}|runs`, () => listRuns(code, 50));

  const chosen = version ?? startVersion;
  const tries = (runs.data ?? []).filter(
    (one) =>
      one.via === "try" &&
      one.version === chosen &&
      (!since || (one.createdAt !== null && new Date(one.createdAt).getTime() >= new Date(since).getTime() - 1000)),
  );
  const latest = tries[0];
  const usual = usualRows((runs.data ?? []).filter((one) => one.via !== "try"));
  const passed = (job.data?.runs ?? []).filter((one) => one.status.toLowerCase() === "passed");

  const go = () => {
    setWorking(true);
    setProblem(null);
    setOutcome(null);
    tryVersion(code, { version: chosen, run: run || null, now: true })
      .then(() => listRuns(code, 50))
      .then(
        (fresh) => {
          runs.reload();
          const made = fresh.find((one) => one.via === "try" && one.version === chosen);
          if (made) onTried?.(made);
        },
        (thrown) => setProblem({ text: errorText(thrown, "The try did not finish"), scout: isScoutDown(thrown) }),
      )
      .finally(() => setWorking(false));
  };

  return (
    <div style={{ display: "grid", gap: "var(--s4)" }}>
      <FieldRow>
        <Field label="Try with">
          <Select value={run} onChange={(event) => setRun(event.target.value)} disabled={working}>
            <option value="">The newest passed run</option>
            {passed.map((one) => (
              <option key={one.id} value={one.id}>
                Run of {when(one.startedAt, timeZone)} ({one.rows.toLocaleString()} rows)
              </option>
            ))}
          </Select>
        </Field>
        {versions.length > 1 && (
          <Field label="Version">
            <Select value={chosen ?? ""} onChange={(event) => setVersion(Number(event.target.value))} disabled={working}>
              {versions.map((one) => (
                <option key={one.number} value={one.number}>
                  {one.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label=" ">
          <div>
            <Button tone="primary" onClick={go} disabled={working || chosen === null}>
              <FlaskConical size={15} aria-hidden />
              {working ? "Trying…" : latest ? "Try again" : "Try"}
            </Button>
          </div>
        </Field>
      </FieldRow>
      {job.error && (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>
          Scout’s list of runs did not load, so only the newest passed run can be tried. {job.error}
        </p>
      )}
      <p style={{ margin: 0, color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
        Nothing is written. The try uses the workflow’s own rules in dry run, so what it says is what a real run would do.
      </p>
      {problem && (
        <Banner
          tone={problem.scout ? "warning" : "danger"}
          title={problem.scout ? "Scout is not answering." : "The try did not finish."}
          actions={<Button onClick={go}>Try again</Button>}
        >
          {problem.text} Your settings are kept.
        </Banner>
      )}
      {working && <SkeletonRows rows={3} columns={3} label="Trying the version on the whole run" />}
      {!working && runs.busy && !runs.data && <SkeletonRows rows={2} columns={3} />}
      {!working && latest && (
        <TryReport
          title={`Version ${latest.version ?? "?"}, tried ${when(latest.createdAt, timeZone)}`}
          rowsCollected={latest.rowsCollected}
          startedAt={latest.scoutStartedAt}
          timeZone={timeZone}
          counts={latest.counts}
          checks={guardChecks(latest.counts, latest.rowsCollected, safety, usual)}
          notes={latest.message}
          onPick={setOutcome}
        >
          <RunRows code={code} runId={latest.id} counts={latest.counts} outcome={outcome} onOutcome={setOutcome} />
        </TryReport>
      )}
      {!working && !latest && runs.data && (
        <p style={{ margin: 0, color: "var(--ink-3)" }}>Not tried yet{since ? " since the draft was saved" : ""}.</p>
      )}
    </div>
  );
}
